import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AllocateSettings,
  AllocateSettingsSelector,
  getAllocateSettings,
  updateAllocateSettings,
} from 'slices/appsAllocateSettings';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  Alert,
  Button,
  DefaultContentLayout,
  Segmented,
  TEInput,
  Typography,
} from '@timeedit/ui-components';
import { Checkbox } from '@timeedit/ui-components/antd';
import intl from '../../../i18n/intl';
import './AllocateSettings.scss';

const GeneralSettingsPage = () => {
  const dispatch = useAppDispatch();
  const settings = useSelector(AllocateSettingsSelector);
  const language = intl.messages as Record<string, string>;
  const authUser = useAppSelector((state: any) => state.auth.user);

  const isRootUser = useMemo(
    () => authUser?.scopes?.includes('TE_ROOT_USER'),
    [authUser]
  );

  useEffect(() => {
    dispatch(getAllocateSettings());
  }, [dispatch]);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const [form, setForm] = useState<AllocateSettings>({});

  const _handleSubmit = () => {
    dispatch(updateAllocateSettings(form));
  };

  const isAutomaticAllocationEnabled =
    form.automaticAllocation?.allocationEnabled ?? false;

  const isAutomaticDeAllocationEnabled =
    form.automaticAllocation?.deAllocationEnabled ?? false;

  const frequency = form.automaticAllocation?.frequency;

  return (
    <DefaultContentLayout
      title={language.allocate_general_settings}
      actions={
        <Button onClick={_handleSubmit} type="primary">
          {language.save_changes}
        </Button>
      }
    >
      <div className="allocate-settings-container">
        <div className="content-box row-box">
          <Typography.Title level={4}>
            {language.automatic_allocation_label}
          </Typography.Title>
          <TEInput.LabelWrapper
            label={language.automatic_allocation_label}
            tooltip={language.automatic_allocation_tooltip}
          >
            <div>
              <Segmented<boolean>
                value={isAutomaticAllocationEnabled}
                options={[
                  {
                    value: false,
                    label: language.disabled,
                  },
                  {
                    value: true,
                    label: language.enabled,
                  },
                ]}
                onChange={(value) =>
                  setForm({
                    ...form,
                    automaticAllocation: {
                      ...form.automaticAllocation,
                      allocationEnabled: value,
                    },
                  })
                }
              />
            </div>
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper
            label={language.automatic_de_allocation_label}
            tooltip={language.automatic_de_allocation_tooltip}
          >
            <div>
              <Segmented<boolean>
                value={isAutomaticDeAllocationEnabled}
                options={[
                  {
                    value: false,
                    label: language.disabled,
                  },
                  {
                    value: true,
                    label: language.enabled,
                  },
                ]}
                onChange={(value) =>
                  setForm({
                    ...form,
                    automaticAllocation: {
                      ...form.automaticAllocation,
                      deAllocationEnabled: value,
                    },
                  })
                }
              />
            </div>
          </TEInput.LabelWrapper>
          <Alert
            className="help-text row-box"
            description={
              <Typography.Text>
                {language.automatic_allocation_info}
                <ul>
                  <li>
                    {language.automatic_allocation_label}:{' '}
                    {language.automatic_allocation_tooltip}
                  </li>
                  <li>
                    {language.automatic_de_allocation_label}:{' '}
                    {language.automatic_de_allocation_tooltip}
                  </li>
                </ul>
              </Typography.Text>
            }
            type="info"
            showIcon={false}
          />
          <TEInput.LabelWrapper
            label={language.frequency_title}
            tooltip={language.frequency_info}
          >
            <div className="frequency-checkboxes">
              <Checkbox
                checked={frequency?.daily ?? false}
                onChange={(e) =>
                  setForm({
                    ...form,
                    automaticAllocation: {
                      ...form.automaticAllocation,
                      frequency: {
                        ...frequency,
                        daily: e.target.checked,
                      },
                    },
                  })
                }
              >
                {language.frequency_daily_label}
              </Checkbox>
              <Checkbox
                checked={frequency?.hourly ?? false}
                onChange={(e) =>
                  setForm({
                    ...form,
                    automaticAllocation: {
                      ...form.automaticAllocation,
                      frequency: {
                        ...frequency,
                        hourly: e.target.checked,
                      },
                    },
                  })
                }
              >
                {language.frequency_hourly_label}
              </Checkbox>
              <Checkbox
                checked={frequency?.everyMinute ?? false}
                disabled={!isRootUser}
                onChange={(e) =>
                  setForm({
                    ...form,
                    automaticAllocation: {
                      ...form.automaticAllocation,
                      frequency: {
                        ...frequency,
                        everyMinute: e.target.checked,
                      },
                    },
                  })
                }
              >
                {language.frequency_every_minute_label}
              </Checkbox>
              <Alert
                className="help-text row-box"
                description={
                  <Typography.Text>
                    {language.frequency_info}
                    <ul>
                      <li>
                        {language.frequency_daily_label}:{' '}
                        {language.frequency_daily_tooltip}
                      </li>
                      <li>
                        {language.frequency_hourly_label}:{' '}
                        {language.frequency_hourly_tooltip}
                      </li>
                      <li>
                        {language.frequency_every_minute_label}:{' '}
                        {language.frequency_every_minute_tooltip}
                      </li>
                    </ul>
                    {language.frequency_manually_info}
                  </Typography.Text>
                }
                type="info"
                showIcon={false}
              />
            </div>
          </TEInput.LabelWrapper>
        </div>
        <div className="content-box row-box">
          <Typography.Title level={4}>{language.waitlist}</Typography.Title>
          <TEInput.LabelWrapper
            label={language.waitlist}
            tooltip={language.manage_waitlist_help}
          >
            <div>
              <Segmented<boolean>
                value={form.enableWaitlist}
                options={[
                  {
                    value: false,
                    label: language.disabled,
                    disabled: !isRootUser,
                  },
                  {
                    value: true,
                    label: language.enabled,
                    disabled: !isRootUser,
                  },
                ]}
                onChange={(value) =>
                  setForm({
                    ...form,
                    enableWaitlist: value,
                  })
                }
              />
            </div>
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper
            label={language.automatic_allocation_from_waitlist_label}
            tooltip={language.automatic_allocation_from_waitlist_tooltip}
          >
            <div>
              <Segmented<boolean>
                value={form.automaticAllocation?.fromWaitlistEnabled ?? false}
                options={[
                  {
                    value: false,
                    label: language.disabled,
                    disabled: !isRootUser,
                  },
                  {
                    value: true,
                    label: language.enabled,
                    disabled: !isRootUser,
                  },
                ]}
                onChange={(value) =>
                  setForm({
                    ...form,
                    automaticAllocation: {
                      ...form.automaticAllocation,
                      fromWaitlistEnabled: value,
                    },
                  })
                }
              />
            </div>
          </TEInput.LabelWrapper>
          <Alert
            className="help-text row-box"
            description={language.manage_waitlist_help}
            type="info"
            showIcon={false}
          />
        </div>
        <div className="content-box row-box">
          <Typography.Title level={4}>
            {language.registration_section}
          </Typography.Title>
          <TEInput.LabelWrapper
            label={language.registerToConflicts}
            tooltip={language.block_student_conflict_tooltip}
          >
            <div>
              <Segmented<boolean>
                options={[
                  {
                    value: false,
                    label: language.allowed,
                  },
                  {
                    value: true,
                    label: language.notallowed,
                  },
                ]}
                value={form.blockStudentConflict}
                onChange={(value) =>
                  setForm({
                    ...form,
                    blockStudentConflict: value,
                  })
                }
              />
            </div>
          </TEInput.LabelWrapper>
          <Alert
            className="help-text row-box"
            description={language.block_student_conflict_help}
            type="info"
            showIcon={false}
          />
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default GeneralSettingsPage;
